import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import debounce from "lodash.debounce";
import { format } from "date-fns";
import Swal from "sweetalert2";
import GenericTableComponent from "../common/Table";
import {
  getAllCampaigns,
  approveRejectCampaign,
  statusCampaign,
  getCpaByCampaignId,
  updateCampaignCpa
} from "../../redux/slices/campaignSlice";
//import { convertToLocalCurrency } from '../../utils/currency';

const columns = [
  { id: "campaignName", label: "Campaign Name" },
  { id: "user", label: "Advertiser name" },
  { id: "createdAt", label: "Date created" },
  // { id: "flow", label: "Flow" },
  // { id: "isActive", label: "Status" },
  { id: "isApproved", label: "Approval" },
  { id: "campaignBudget", label: "Budget" },
];

const RejectedCampaign = () => {
  const dispatch = useDispatch();
  const { campaigns, meta, loading } = useSelector((state) => state.campaign);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({});
  const userRole = localStorage.getItem('userRole');
  const approval = "REJECTED";

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      const startDate = format(dateRange.startDate, "yyyy-MM-dd");
      const endDate = format(dateRange.endDate, "yyyy-MM-dd");
      dispatch(
        getAllCampaigns({
          page: 0,
          rowsPerPage,
          state: approval,
          startDate,
          endDate,
        })
      );
    } else {
      dispatch(getAllCampaigns({ page, rowsPerPage, state: approval }));
    }
  }, [dateRange, dispatch, page, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm) => {
        dispatch(getAllCampaigns({ page: 0, rowsPerPage, name: searchTerm }));
      }, 2000),
    [dispatch, rowsPerPage]
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const triggerSearch = () => {
    dispatch(
      getAllCampaigns({ page: 0, rowsPerPage, name: search, state: approval })
    );
  };

  const handleFilterChange = (value) => {
    let boolValue;
    if (value === "ACTIVATED") {
      boolValue = true;
    } else if (value === "DEACTIVATED") {
      boolValue = false;
    } else {
      boolValue = null;
    }
    dispatch(
      getAllCampaigns({
        page: 0,
        rowsPerPage,
        state: approval,
        status: boolValue,
      })
    );
  };

  const handleApproveClick = (id) => {
    Swal.fire({
      title: "Approve Campaign",
      html: `
        <label for="publisherCPA" style="display:block; margin-bottom:5px;">Publisher CPA</label>
        <input id="publisherCPA" type="text" class="swal2-input" placeholder="Enter Publisher CPA">
        <label for="campaignCPA" style="display:block; margin-top:15px; margin-bottom:5px;">Campaign CPA</label>
        <input id="campaignCPA" type="text" class="swal2-input" placeholder="Enter Campaign CPA">
      `,
      confirmButtonText: "Approve",
      showCancelButton: true,
      preConfirm: () => {
        const publisherCPA = document.getElementById("publisherCPA").value;
        const campaignCPA = document.getElementById("campaignCPA").value;
  
        if (!publisherCPA || !campaignCPA) {
          Swal.showValidationMessage("Both CPA values are required!");
          return;
        }
  
        return {
          state: "APPROVED",
          publisherCPA: publisherCPA,
          campaignCPA: campaignCPA,
          note: "",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        //console.log(result)
        //return
        dispatch(approveRejectCampaign({ id, ...result.value }))
          .unwrap()
          .then(() => {
            dispatch(getAllCampaigns({ page, rowsPerPage }));
          })
          .catch((error) => {
            console.error("Failed to approve campaign:", error);
          });
      }
    });
  };
  
  const handleRejectClick = (id) => {
    Swal.fire({
      title: "Reject Campaign",
      html: `
        <label for="reason" style="display:block; margin-bottom:5px;">Reason</label>
        <textarea id="reason" class="swal2-textarea" placeholder="Enter reason for rejection"></textarea>
      `,
      confirmButtonText: "Reject",
      showCancelButton: true,
      preConfirm: () => {
        const note = document.getElementById("reason").value;
  
        if (!note) {
          Swal.showValidationMessage("Reason is required!");
          return;
        }
  
        return {
          state: "REJECTED",
          publisherCPA: "0",
          campaignCPA: "0",
          note,
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        //console.log(result)
        //return
        dispatch(approveRejectCampaign({ id, ...result.value }))
          .unwrap()
          .then(() => {
            dispatch(getAllCampaigns({ page, rowsPerPage }));
          })
          .catch((error) => {
            console.error("Failed to reject campaign:", error);
          });
      }
    });
  };

  const handlePause = (id) => {
    dispatch(statusCampaign(id))
      .unwrap()
      .then(() => {
        dispatch(getAllCampaigns({ page, rowsPerPage, state: approval }));
      })
      .catch((error) => {
        console.error("Failed to reject campaign:", error);
      });
};

const updateCpa = async (id) => {
  await dispatch(getCpaByCampaignId(id)).then((action) => {
    if (action.payload?.statusCode === 200) {
      const { costPerUserCampaign, costPerUserPublisher } = action.payload.data;

      Swal.fire({
        title: "Update CPA",
        html: `
          <label for="publisherCPA" style="display:block; margin-bottom:5px;">Publisher CPA</label>
          <input id="publisherCPA" type="text" class="swal2-input" value="${costPerUserPublisher}" placeholder="Enter Publisher CPA">
          <label for="campaignCPA" style="display:block; margin-top:15px; margin-bottom:5px;">Campaign CPA</label>
          <input id="campaignCPA" type="text" class="swal2-input" value="${costPerUserCampaign}" placeholder="Enter Campaign CPA">
        `,
        confirmButtonText: "Update",
        showCancelButton: true,
        preConfirm: () => {
          const publisherCPA = document.getElementById("publisherCPA").value;
          const campaignCPA = document.getElementById("campaignCPA").value;

          return {
            costPerUserPublisher: parseFloat(publisherCPA),
            costPerUserCampaign: parseFloat(campaignCPA),
          };
        },
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(updateCampaignCpa({ id, ...result.value }))
            .unwrap()
            .then(() => {
              dispatch(getAllCampaigns({ page, rowsPerPage }));
              dispatch(getCpaByCampaignId(id));
              Swal.fire("Success", "CPA updated successfully!", "success");
            })
            .catch((error) => {
              console.error("Failed to update CPA:", error);
            });
        }
      });
    } else {
      console.error("Failed to fetch CPA data:", action.error);
      Swal.fire("Oops..", "Failed to fetch CPA data", "error");
    }
  });
};

  return (
    <div>
      {loading ? (
        <div> </div>
      ) : (
        <GenericTableComponent
          columns={columns}
          data={campaigns}
          filterSearch={true}
          paginate={true}
          viewLink="/view-campaign"
          editLink="/edit-campaign"
          publisherLink="/publisher-link"
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={handlePageChange}
          triggerSearch={triggerSearch}
          search={search}
          handleRowsPerPageChange={handleRowsPerPageChange}
          handleSearchChange={handleSearchChange}
          totalItems={meta?.itemCount || 0}
          handleApproveClick={handleApproveClick}
          handleRejectClick={handleRejectClick}
          handlePause={handlePause}
          updateCpa={userRole === 'SUPPORT' ? false : updateCpa}
          hideEdit={userRole === 'SUPPORT' ? true : false}
          hideApprove={userRole === 'SUPPORT' ? true : false}
          hideReject={userRole === 'SUPPORT' ? true : false}
          setDateRange={setDateRange}
          dateRange={dateRange}
          onFilterChange={handleFilterChange}
          hideActiveSearch
        />
      )}
    </div>
  );
};

export default RejectedCampaign;
